import { Link } from 'react-router-dom';
import { SupportButton } from '../../components/SupportButton';
import { Bell, Car, ChartColumn, Percent, ChevronRight, Users, Clock, FileCheck2, Flame, HandCoins, Repeat, Settings, Shapes, ShieldAlert, UserRound, Wallet, type LucideIcon, QrCode } from 'lucide-react';
import { Card } from '@tcrd/ui';
import { Page } from '../../components/Page';
import { useAuth } from '../../stores/auth';
import { AccountSettings } from '../common/AccountSettings';
import { AlertTestCard } from './AlertTestCard';
import { SafetySettings } from '../safety/SafetySettings';
import { useMyCommissions } from './CommissionsPage';

export interface MenuItem {
  to: string;
  label: string;
  icon: LucideIcon;
  hint?: string;
  adminOnly?: boolean;
  /** Solo si la comisión semanal está activa. */
  commissionOnly?: boolean;
}

/** Secciones del panel del conductor. Cada fase agrega sus entradas aquí. */
export const DRIVER_MENU: Array<{ group: string; items: MenuItem[] }> = [
  {
    group: 'Operación',
    items: [
      { to: '/driver/clients', label: 'Mis clientes', icon: Users, hint: 'Cuántos tienes, su último viaje y cómo contactarlos' },
      { to: '/driver/invite', label: 'Mi enlace y QR', icon: QrCode, hint: 'Compártelo: quien se registre será tu cliente' },
      { to: '/driver/demand', label: 'Zonas con demanda', icon: Flame, hint: 'Dónde suelen pedir viajes a esta hora' },
      { to: '/driver/recurring', label: 'Viajes recurrentes', icon: Repeat },
      { to: '/driver/vehicles', label: 'Mis vehículos', icon: Car },
      { to: '/driver/documents', label: 'Mis documentos', icon: FileCheck2, hint: 'Licencia, seguro, matrícula' },
      { to: '/driver/notifications', label: 'Notificaciones', icon: Bell },
    ],
  },
  {
    group: 'Finanzas',
    items: [
      { to: '/driver/payments', label: 'Cobros', icon: Wallet, hint: 'Pendientes y registrados' },
      { to: '/driver/tips', label: 'Propinas', icon: HandCoins },
      { to: '/driver/reports', label: 'Reportes', icon: ChartColumn, hint: 'Ingresos, exportar CSV/PDF' },
      { to: '/driver/commissions', label: 'Comisión semanal', icon: Percent, hint: 'Se calcula cada lunes', commissionOnly: true },
    ],
  },
  {
    group: 'Mi cuenta',
    items: [
      { to: '/driver/profile', label: 'Perfil profesional', icon: UserRound },
      { to: '/driver/availability', label: 'Disponibilidad', icon: Clock, hint: 'Horario semanal' },
    ],
  },
  {
    group: 'Negocio',
    items: [
      { to: '/driver/drivers', label: 'Conductores', icon: Users, hint: 'Invitar y gestionar', adminOnly: true },
      { to: '/driver/commissions-admin', label: 'Comisiones', icon: Percent, hint: 'Verificar pagos de los conductores', adminOnly: true },
      { to: '/driver/incidents', label: 'Seguridad y reportes', icon: ShieldAlert, hint: 'Emergencias, reportes y objetos perdidos', adminOnly: true },
      { to: '/driver/services', label: 'Servicios', icon: Shapes, hint: 'Habilitar o deshabilitar', adminOnly: true },
      { to: '/driver/settings', label: 'Configuración', icon: Settings, hint: 'Tarifas, políticas, landing', adminOnly: true },
    ],
  },
];

export function DriverMore() {
  const isAdmin = useAuth((s) => s.hasRole('TENANT_ADMIN'));
  const commissions = useMyCommissions();
  const commissionOn = !!commissions.data?.enabled || !!commissions.data?.items.length;
  return (
    <Page title="Más">
      <div className="flex flex-col gap-6">
        {DRIVER_MENU.map((g) => {
          const items = g.items.filter((i) => (!i.adminOnly || isAdmin) && (!i.commissionOnly || (commissionOn && !isAdmin)));
          if (!items.length) return null;
          return (
            <section key={g.group}>
              <h2 className="px-1 mb-2 text-xs font-semibold uppercase tracking-wide text-[var(--text-muted)]">{g.group}</h2>
              <Card className="divide-y divide-[var(--border)] overflow-hidden">
                {items.map((i) => (
                  <Link key={i.to} to={i.to} className="tap flex items-center gap-3 px-4 py-3.5 hover:bg-black/[0.03] dark:hover:bg-white/5">
                    <i.icon className="size-5 text-[var(--text-muted)]" aria-hidden />
                    <span className="flex-1">
                      <span className="block font-medium">{i.label}</span>
                      {i.hint && <span className="block text-xs text-[var(--text-muted)]">{i.hint}</span>}
                    </span>
                    <ChevronRight className="size-5 text-[var(--text-muted)]" aria-hidden />
                  </Link>
                ))}
              </Card>
            </section>
          );
        })}
        {!isAdmin && <SupportButton base="/driver" />}
        <AlertTestCard />
        <SafetySettings customer={false} />
        <AccountSettings />
      </div>
    </Page>
  );
}
